import {
  Box,
  Card,
  CardContent,
  Divider,
  List,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import LeaderboardOutlined from "@mui/icons-material/LeaderboardOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import {
  GetTopSalesResponse,
  TopSales,
} from "subModule/src/services/analytics/interface";
import { AnalyticsProfileService } from "subModule/src/core/services";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
interface TopSalesGarageProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function TopSalesGarage(props: TopSalesGarageProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [parts, setParts] = useState<TopSales[]>([]);
  const [services, setServices] = useState<TopSales[]>([]);
  const [partsloading, setPartsLoading] = useState<boolean>(true);
  const [servicesloading, setServicesLoading] = useState<boolean>(true);
  const [tab, setTab] = useState<number>(0);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      fetchParts("topsalesitems");
      setTimeout(() => fetchServices("topsalesservice"), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      fetchParts("topsalesitems");
      setTimeout(() => fetchServices("topsalesservice"), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchParts = (type: string) => {
    setPartsLoading(true);
    AnalyticsProfileService.getTopSales(
      {
        stationId: watch("stationId")?.value || null,
        range: watch("range")?.value,
        allStation: watch("allStation"),
      },
      type,
    )
      .then((res: GetTopSalesResponse | unknown | any) => {
        if (res?.success) {
          setParts(res?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setPartsLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  const fetchServices = (type: string) => {
    setServicesLoading(true);
    AnalyticsProfileService.getTopSales(
      {
        stationId: watch("stationId")?.value || null,
        range: watch("range")?.value,
        allStation: watch("allStation"),
      },
      type,
    )
      .then((res: GetTopSalesResponse | unknown | any) => {
        if (res?.success) {
          setServices(res?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setServicesLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  const canShowParts = checkPrivileges("ROLE_ANALYTICS_TOP_SALES_ITEMS");
  const canShowServices = checkPrivileges("ROLE_ANALYTICS_TOP_SALES_SERVICES");
  const showToggle = canShowParts && canShowServices;
  const toggleTrack = props.themes?.primary || "#1F2A44";

  return (
    <Card
      sx={{
        height: "270px",
        marginTop: "16px",
        boxShadow: "none",
        borderRadius: "8px",
        "& .MuiCardContent-root": { padding: "16px !important" },
      }}
    >
      <CardContent>
        <Box sx={dashboardWidgetHeaderSx}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <AnalyticsWidgetHeader
              title={t("topSales")}
              icon={<LeaderboardOutlined />}
              iconBg={`${props.themes?.primary || "#4F46E5"}18`}
              iconColor={props.themes?.primary || "#4F46E5"}
            />
            {showToggle ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  p: "3px",
                  borderRadius: 999,
                  backgroundColor: toggleTrack,
                  gap: "2px",
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => setTab(0)}
                  sx={{
                    border: 0,
                    cursor: "pointer",
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.4,
                    minWidth: 64,
                    fontFamily: "Saira, sans-serif",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    transition: "background-color 0.2s ease, color 0.2s ease",
                    backgroundColor: tab === 0 ? "#fff" : "transparent",
                    color: tab === 0 ? toggleTrack : "#fff",
                  }}
                >
                  {t("items")}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setTab(1)}
                  sx={{
                    border: 0,
                    cursor: "pointer",
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.4,
                    minWidth: 64,
                    fontFamily: "Saira, sans-serif",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    transition: "background-color 0.2s ease, color 0.2s ease",
                    backgroundColor: tab === 1 ? "#fff" : "transparent",
                    color: tab === 1 ? toggleTrack : "#fff",
                  }}
                >
                  {t("services")}
                </Box>
              </Box>
            ) : null}
          </Box>
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={partsloading || servicesloading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      fetchParts("topsalesitems");
                      setTimeout(() => fetchServices("topsalesservice"), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "topSales"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "topSales",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "topSales",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions("topSales", "loadNext");
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      fetchParts("topsalesitems");
                      setTimeout(() => fetchServices("topsalesservice"), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={partsloading || servicesloading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        {partsloading || servicesloading ? (
          <>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mt: 1.5 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </>
        ) : (
          <>
            {tab === 0 && canShowParts && (
              <Box
                role="tabpanel"
                className="report-tab"
                id="simple-tabpanel-0"
                aria-labelledby={`simple-tab-0`}
              >
                <List dense className="h-[180px] overflow-y-auto p-0 mt-[12px]">
                  {(parts && parts?.length === 0) || !parts ? (
                    <Box className="flex justify-center flex-col">
                      <Typography
                        className="font-Saira text-center italic font-semibold mt-[40px]"
                        sx={{ color: props.themes.primary }}
                      >
                        {t("noRecordFound")}
                      </Typography>
                    </Box>
                  ) : (
                    parts?.map((item: TopSales) => (
                      <>
                        <li style={{ width: "95%", paddingBlock: 10 }}>
                          <Typography
                            paragraph
                            className={`mb-0 flex gap-2 items-end justify-between font-Saira text-[13px]`}
                          >
                            <Tooltip
                              followCursor
                              title={
                                item.ItemName +
                                (item.ItemCode ? " | " + item.ItemCode : "")
                              }
                            >
                              <span
                                style={{
                                  padding: "2px 4px",
                                  borderRadius: "8px",
                                  backgroundColor: `${props.themes?.primary || "#4F46E5"}14`,
                                  color: props.themes?.primary || "#4F46E5",
                                  fontFamily: "Saira, sans-serif",
                                  fontSize: 11,
                                  fontWeight: 800,
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                  // maxWidth: 90,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                }}
                                // className={`font-semibold text-left text-ellipsis overflow-hidden w-[100%] max-w-[fit-content] whitespace-nowrap`}
                              >
                                {item.ItemName +
                                  (item.ItemCode ? " | " + item.ItemCode : "")}
                              </span>
                            </Tooltip>
                          </Typography>
                          <Typography
                            paragraph
                            className={`mb-0 flex justify-between font-Saira text-[13px]`}
                          >
                            <Tooltip
                              followCursor
                              title={item?.TotalPrice?.toLocaleString() || 0}
                            >
                              <Box className={`flex gap-1 items-baseline`}>
                                <span className={`font-semibold`}>
                                  {new Intl.NumberFormat("en", {
                                    notation: "compact",
                                    compactDisplay: "short",
                                    maximumFractionDigits: 2,
                                  }).format(Math.abs(item?.TotalPrice || 0))}
                                </span>
                                <span
                                  className={`text-[11px] uppercase font-medium`}
                                >
                                  {props?.user?.business?.currency || 0}
                                </span>
                              </Box>
                            </Tooltip>
                            <Box className={`flex gap-1`}>
                              <span className="italic">{t("count")}:</span>
                              <span className="font-semibold">{item.Size}</span>
                            </Box>
                          </Typography>
                        </li>
                        <Divider />
                      </>
                    ))
                  )}
                </List>
              </Box>
            )}
            {tab === (canShowParts ? 1 : 0) && canShowServices && (
              <Box
                role="tabpanel"
                className="report-tab"
                id="simple-tabpanel-1"
                aria-labelledby={`simple-tab-1`}
              >
                <List dense className="h-[180px] overflow-y-auto p-0 mt-[12px]">
                  {(services && services?.length === 0) || !services ? (
                    <Box className="flex justify-center flex-col">
                      <Typography
                        className="font-Saira text-center italic font-semibold mt-[40px]"
                        sx={{ color: props.themes.primary }}
                      >
                        {t("noRecordFound")}
                      </Typography>
                    </Box>
                  ) : (
                    services?.map((item: TopSales) => (
                      <>
                        <li style={{ width: "95%", paddingBlock: 10 }}>
                          <Typography
                            paragraph
                            className={`mb-0 flex gap-2 items-end justify-between font-Saira text-[13px]`}
                          >
                            <Tooltip
                              followCursor
                              title={
                                item.ServiceName +
                                (item.ServiceCode
                                  ? " | " + item.ServiceCode
                                  : "")
                              }
                            >
                              <span
                                className={`text-left text-ellipsis overflow-hidden w-[100%] max-w-[fit-content] whitespace-nowrap`}
                              >
                                {item.ServiceName +
                                  (item.ServiceCode
                                    ? " | " + item.ServiceCode
                                    : "")}
                              </span>
                            </Tooltip>
                          </Typography>
                          <Typography
                            paragraph
                            className={`mb-0 flex justify-between font-Saira text-[13px]`}
                          >
                            <Tooltip
                              followCursor
                              title={item?.TotalPrice?.toLocaleString() || 0}
                            >
                              <Box className={`flex gap-1 items-baseline`}>
                                <span className={`font-semibold`}>
                                  {new Intl.NumberFormat("en", {
                                    notation: "compact",
                                    compactDisplay: "short",
                                    maximumFractionDigits: 2,
                                  }).format(Math.abs(item?.TotalPrice || 0))}
                                </span>
                                <span
                                  className={`text-[11px] uppercase font-medium`}
                                >
                                  {props?.user?.business?.currency || 0}
                                </span>
                              </Box>
                            </Tooltip>
                            <Box className={`flex gap-1`}>
                              <span className="italic">{t("count")}:</span>
                              <span className="font-semibold">{item.Size}</span>
                            </Box>
                          </Typography>
                        </li>
                        <Divider />
                      </>
                    ))
                  )}
                </List>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
